// A SceneObject is one movable "thing" in the scene — toward the floor
// planner, this is a piece of furniture: its own voxel grid (the shape,
// authored in modeling mode) plus a continuous world transform (its
// placement in the room, set in placement mode). The transform lives on
// `group`, which parents the object's mesh; `group` is a child of the
// scene's gridGroup so the room transform (VR placement / nav / resize)
// composes over every object.
//
// This replaces the app's original single-global-grid assumption: the
// scene is now a list of these, with one "active" object that modeling
// edits. Phase 1 migrates the existing grid into a single SceneObject at
// the identity transform, so behavior is unchanged until more objects and
// placement mode land.

import * as THREE from "three";
import { type PaletteLookup, VoxelMesher } from "../view/voxel-mesh";
import { VoxelGrid } from "./voxel-grid";

export interface SceneObject {
  readonly id: number;
  // Shape, in object-local integer cell coordinates.
  readonly grid: VoxelGrid;
  // Renders `grid`; its object3D is parented to `group`.
  readonly mesher: VoxelMesher;
  // Continuous placement transform (position + yaw). Identity until the
  // object is moved in placement mode. Child of gridGroup.
  readonly group: THREE.Group;
}

let nextSceneObjectId = 1;

// Wrap an already-constructed grid + mesher as a SceneObject, parenting
// the mesher's mesh under a fresh transform group. Caller adds `group`
// to the scene (gridGroup).
export function makeSceneObject(
  grid: VoxelGrid,
  mesher: VoxelMesher,
): SceneObject {
  const group = new THREE.Group();
  group.add(mesher.object3D);
  return { id: nextSceneObjectId++, grid, mesher, group };
}

// Create a fresh empty object (its own grid + mesher) at the identity
// transform. Used when sketching on the room floor needs a new
// room-aligned object to draw into. Caller adds `group` to gridGroup.
export function createSceneObject(palette: PaletteLookup): SceneObject {
  return makeSceneObject(new VoxelGrid(), new VoxelMesher(palette));
}
